import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsIcon from "@mui/icons-material/Groups";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import {
  listContenidoAreas,
  listContenidoBranches,
  listContenidoRoles,
} from "../api/contenidoApi";
import {
  createEvaluacion,
  getEvaluacion,
  previewEvaluacionScope,
  updateEvaluacion,
} from "../api/evaluacionesApi";
import EvaluacionBodyBuilder, {
  EVALUATION_TOTAL_POINTS,
  getQuestionsTotalPoints,
  makeQuestion,
  normalizeQuestionFromApi,
} from "../components/EvaluacionBodyBuilder";

const STATUS_OPTIONS = ["DRAFT", "PUBLISHED", "ARCHIVED"];
const STATUS_LABELS = {
  DRAFT: "Borrador",
  PUBLISHED: "Publicado",
  ARCHIVED: "Archivado",
};

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function readCurrentUserDisplayName() {
  try {
    const raw = localStorage.getItem("sabi_user");
    if (!raw) return "";

    const user = JSON.parse(raw);
    return user?.displayName || user?.display_name || user?.name || "";
  } catch {
    return "";
  }
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function splitDateTimeForForm(value, mode = "start") {
  if (!value) {
    return {
      date: "",
      time: mode === "end" ? "23:59" : "00:00",
    };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return {
      date: "",
      time: mode === "end" ? "23:59" : "00:00",
    };
  }

  const dateValue = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  const timeValue = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

  return { date: dateValue, time: timeValue };
}

function buildLocalDate(dateValue, timeValue, fallbackTime) {
  if (!dateValue) return null;

  const [year, month, day] = String(dateValue).split("-").map(Number);
  const [hour, minute] = String(timeValue || fallbackTime).split(":").map(Number);

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function buildIsoDateTime(dateValue, timeValue, fallbackTime) {
  const date = buildLocalDate(dateValue, timeValue, fallbackTime);
  return date ? date.toISOString() : null;
}

function renderSelectedNames(selected, options, emptyLabel) {
  if (!Array.isArray(selected) || selected.length === 0) return emptyLabel;

  const names = selected
    .map((id) => options.find((item) => String(item.id) === String(id))?.name)
    .filter(Boolean);

  return names.length ? names.join(", ") : emptyLabel;
}

function ScopeEmployeesModal({
  open,
  loading,
  employees,
  selectedEmployeeIds,
  onToggleEmployee,
  onClose,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Seleccionar empleados</DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
            Cargando empleados...
          </Typography>
        ) : employees.length === 0 ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "neutral.700" }}>
            No hay empleados activos para mostrar.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {employees.map((employee) => {
              const checked =
                employee.isSelected ||
                employee.isSelectedByScope ||
                selectedEmployeeIds.includes(String(employee.id));

              return (
                <Box
                  key={employee.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "48px 1.4fr 1fr 1fr 1fr",
                    gap: 1,
                    alignItems: "center",
                    px: 1.5,
                    py: 1,
                    borderRadius: "14px",
                    bgcolor: checked ? "secondary.100" : "neutral.50",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={employee.isSelectedByScope}
                    onChange={() => onToggleEmployee(employee.id)}
                  />

                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
                    {employee.fullName}
                  </Typography>

                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.700" }}>
                    {employee.branchName || "-"}
                  </Typography>

                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.700" }}>
                    {employee.areaNames?.join(", ") || "-"}
                  </Typography>

                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.700" }}>
                    {employee.isSelectedByScope
                      ? "Incluido por alcance"
                      : selectedEmployeeIds.includes(String(employee.id))
                      ? "Seleccionado manual"
                      : "No seleccionado"}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EvaluacionFormPage() {
  const navigate = useNavigate();
  const { evaluationId } = useParams();
  const isEditing = Boolean(evaluationId);

  const [loading, setLoading] = useState(isEditing);
  const [loadingReferences, setLoadingReferences] = useState(true);
  const [saving, setSaving] = useState(false);

  const [evaluation, setEvaluation] = useState(null);
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);

  const [form, setForm] = useState({
    title: "",
    status: "DRAFT",
    availableFromDate: "",
    availableFromTime: "00:00",
    availableUntilDate: "",
    availableUntilTime: "23:59",
    noTimeLimit: true,
    timeLimitMinutes: "",
  });

  const [scopeAll, setScopeAll] = useState(true);
  const [scopeBranch, setScopeBranch] = useState(false);
  const [scopeArea, setScopeArea] = useState(false);
  const [scopeRole, setScopeRole] = useState(false);

  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

  const [questions, setQuestions] = useState([makeQuestion()]);

  const [employeesModalOpen, setEmployeesModalOpen] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesPreviewLoading, setEmployeesPreviewLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeesTotal, setSelectedEmployeesTotal] = useState(0);

  const authorLabel =
    evaluation?.author?.displayName || readCurrentUserDisplayName() || "-";

  const scopePayload = useMemo(
    () => ({
      applyToAllCompany: scopeAll,
      branchIds: scopeAll || !scopeBranch ? [] : selectedBranchIds.map(Number),
      areaIds: scopeAll || !scopeArea ? [] : selectedAreaIds.map(Number),
      roleIds: scopeAll || !scopeRole ? [] : selectedRoleIds.map(Number),
      employeeIds: selectedEmployeeIds.map(Number),
    }),
    [
      scopeAll,
      scopeBranch,
      scopeArea,
      scopeRole,
      selectedBranchIds,
      selectedAreaIds,
      selectedRoleIds,
      selectedEmployeeIds,
    ]
  );

  useEffect(() => {
    let alive = true;

    const timeout = setTimeout(async () => {
      if (!alive) return;

      setEmployeesPreviewLoading(true);

      try {
        const data = await previewEvaluacionScope(scopePayload);
        if (!alive) return;

        const nextEmployees = Array.isArray(data?.employees) ? data.employees : [];

        setEmployees(nextEmployees);

        const nextTotal =
          typeof data?.totalSelected === "number"
            ? data.totalSelected
            : nextEmployees.filter((employee) => employee.isSelected).length;

        setSelectedEmployeesTotal(nextTotal);
      } catch {
        if (!alive) return;
        setEmployees([]);
        setSelectedEmployeesTotal(0);
      } finally {
        if (alive) setEmployeesPreviewLoading(false);
      }
    }, 350);

    return () => {
      alive = false;
      clearTimeout(timeout);
    };
  }, [scopePayload]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const [branchesData, areasData, rolesData] = await Promise.all([
          listContenidoBranches(),
          listContenidoAreas(),
          listContenidoRoles(),
        ]);

        if (!alive) return;

        setBranches(branchesData);
        setAreas(areasData);
        setRoles(rolesData);
      } catch (err) {
        if (!alive) return;
        window.alert(mapApiError(err).message);
      } finally {
        if (alive) setLoadingReferences(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !evaluationId) {
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const data = await getEvaluacion(evaluationId);

        if (!alive) return;

        setEvaluation(data);

        const availableFromParts = splitDateTimeForForm(data.availableFrom, "start");
        const availableUntilParts = splitDateTimeForForm(data.availableUntil, "end");

        setForm({
          title: data.title ?? "",
          status: data.status ?? "DRAFT",
          availableFromDate: availableFromParts.date,
          availableFromTime: availableFromParts.time,
          availableUntilDate: availableUntilParts.date,
          availableUntilTime: availableUntilParts.time,
          noTimeLimit: !data.timeLimitMinutes,
          timeLimitMinutes: data.timeLimitMinutes ? String(data.timeLimitMinutes) : "",
        });

        const branchIds = Array.isArray(data.branchIds) ? data.branchIds.map(String) : [];
        const areaIds = Array.isArray(data.areaIds) ? data.areaIds.map(String) : [];
        const roleIds = Array.isArray(data.roleIds) ? data.roleIds.map(String) : [];
        const employeeIds = Array.isArray(data.employeeIds) ? data.employeeIds.map(String) : [];

        setScopeAll(Boolean(data.applyToAllCompany));
        setScopeBranch(branchIds.length > 0);
        setScopeArea(areaIds.length > 0);
        setScopeRole(roleIds.length > 0);

        setSelectedBranchIds(branchIds);
        setSelectedAreaIds(areaIds);
        setSelectedRoleIds(roleIds);
        setSelectedEmployeeIds(employeeIds);

        setQuestions(
          Array.isArray(data.questions) && data.questions.length
            ? data.questions.map(normalizeQuestionFromApi)
            : [makeQuestion()]
        );
      } catch (err) {
        if (!alive) return;
        window.alert(mapApiError(err).message);
        navigate("/rrhh/contenido");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [evaluationId, isEditing, navigate]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleToggleAll = (event) => {
    const checked = event.target.checked;
    setScopeAll(checked);

    if (checked) {
      setScopeBranch(false);
      setScopeArea(false);
      setScopeRole(false);
      setSelectedBranchIds([]);
      setSelectedAreaIds([]);
      setSelectedRoleIds([]);
    }
  };

  const refreshEmployeesPreview = async ({ openModal = false } = {}) => {
    if (openModal) {
      setEmployeesModalOpen(true);
      setEmployeesLoading(true);
    } else {
      setEmployeesPreviewLoading(true);
    }

    try {
      const data = await previewEvaluacionScope(scopePayload);
      const nextEmployees = Array.isArray(data?.employees) ? data.employees : [];

      setEmployees(nextEmployees);

      const nextTotal =
        typeof data?.totalSelected === "number"
          ? data.totalSelected
          : nextEmployees.filter((employee) => employee.isSelected).length;

      setSelectedEmployeesTotal(nextTotal);
    } catch (err) {
      setEmployees([]);
      setSelectedEmployeesTotal(0);

      if (openModal) {
        window.alert(mapApiError(err).message);
      }
    } finally {
      if (openModal) {
        setEmployeesLoading(false);
      } else {
        setEmployeesPreviewLoading(false);
      }
    }
  };

  const loadEmployeesPreview = () => {
    refreshEmployeesPreview({ openModal: true });
  };

  const toggleManualEmployee = (employeeId) => {
    const id = String(employeeId);

    setSelectedEmployeeIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const validateBeforeSave = () => {
    const title = form.title.trim();

    if (!title) {
      window.alert("El título es obligatorio.");
      return false;
    }

    const hasSomeAvailabilityDate = Boolean(form.availableFromDate || form.availableUntilDate);

    if (form.status === "PUBLISHED" || hasSomeAvailabilityDate) {
      if (!form.availableFromDate || !form.availableUntilDate) {
        window.alert("La fecha de inicio y la fecha de finalización son obligatorias.");
        return false;
      }

      const availableFromDate = buildLocalDate(
        form.availableFromDate,
        form.availableFromTime,
        "00:00"
      );

      const availableUntilDate = buildLocalDate(
        form.availableUntilDate,
        form.availableUntilTime,
        "23:59"
      );

      if (!availableFromDate || !availableUntilDate) {
        window.alert("Las fechas de disponibilidad no son válidas.");
        return false;
      }

      if (availableUntilDate <= availableFromDate) {
        window.alert("La fecha de finalización debe ser posterior a la fecha de inicio.");
        return false;
      }
    }

    if (!form.noTimeLimit) {
      const timeLimit = Number(form.timeLimitMinutes);

      if (!Number.isFinite(timeLimit) || timeLimit < 1) {
        window.alert("Indicá un tiempo límite válido o marcá 'Sin tiempo límite'.");
        return false;
      }
    }

    const totalPoints = getQuestionsTotalPoints(questions);

    if (Math.abs(totalPoints - EVALUATION_TOTAL_POINTS) > 0.01) {
      window.alert(
        `El puntaje total de la evaluación debe sumar ${EVALUATION_TOTAL_POINTS} puntos. Actualmente suma ${totalPoints.toFixed(2)}.`
      );
      return false;
    }

    if (!scopeAll && scopeBranch && selectedBranchIds.length === 0) {
      window.alert("Seleccioná al menos una sucursal.");
      return false;
    }

    if (!scopeAll && scopeArea && selectedAreaIds.length === 0) {
      window.alert("Seleccioná al menos un área.");
      return false;
    }

    if (!scopeAll && scopeRole && selectedRoleIds.length === 0) {
      window.alert("Seleccioná al menos un rol.");
      return false;
    }

    if (!questions.length) {
      window.alert("La evaluación debe tener al menos una pregunta.");
      return false;
    }

    for (const [index, question] of questions.entries()) {
      if (!question.question.trim()) {
        window.alert(`La pregunta ${index + 1} no tiene encabezado.`);
        return false;
      }

      const points = Number(question.points ?? 1);

      if (!Number.isFinite(points) || points < 0) {
        window.alert(`La pregunta ${index + 1} debe tener un puntaje mayor o igual a 0.`);
        return false;
      }

      const isSelection =
        question.questionType === "SINGLE_CHOICE" ||
        question.questionType === "MULTIPLE_CHOICE";

      if (isSelection) {
        if (!question.options || question.options.length < 2) {
          window.alert(`La pregunta ${index + 1} debe tener al menos dos opciones.`);
          return false;
        }

        if (question.options.some((option) => !option.text.trim())) {
          window.alert(`Todas las opciones de la pregunta ${index + 1} deben tener texto.`);
          return false;
        }

        if (!question.correct || question.correct.length === 0) {
          window.alert(`La pregunta ${index + 1} debe tener al menos una respuesta correcta.`);
          return false;
        }

        if (question.questionType === "SINGLE_CHOICE" && question.correct.length !== 1) {
          window.alert(`La pregunta ${index + 1} debe tener exactamente una respuesta correcta.`);
          return false;
        }
      }
    }

    return true;
  };

  const buildPayload = () => {
    return {
      title: form.title.trim(),
      status: form.status,
      applyToAllCompany: scopeAll,
      branchIds: scopeAll || !scopeBranch ? [] : selectedBranchIds.map(Number),
      areaIds: scopeAll || !scopeArea ? [] : selectedAreaIds.map(Number),
      roleIds: scopeAll || !scopeRole ? [] : selectedRoleIds.map(Number),
      employeeIds: selectedEmployeeIds.map(Number),
      availableFrom: buildIsoDateTime(
        form.availableFromDate,
        form.availableFromTime,
        "00:00"
      ),
      availableUntil: buildIsoDateTime(
        form.availableUntilDate,
        form.availableUntilTime,
        "23:59"
      ),
      timeLimitMinutes: form.noTimeLimit ? null : Number(form.timeLimitMinutes),
      questions: questions.map((question, index) => {
        const isSelection =
          question.questionType === "SINGLE_CHOICE" ||
          question.questionType === "MULTIPLE_CHOICE";

        const base = {
          question: question.question.trim(),
          questionType: question.questionType,
          orderIndex: index + 1,
          points: Number(question.points ?? 0),
        };

        if (!isSelection) return base;

        return {
          ...base,
          options: question.options.map((option) => ({
            id: option.id,
            text: option.text.trim(),
          })),
          correct: question.correct,
        };
      }),
    };
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;

    setSaving(true);

    try {
      const payload = buildPayload();

      if (isEditing && evaluationId) {
        await updateEvaluacion(evaluationId, payload);
      } else {
        await createEvaluacion(payload);
      }

      navigate("/rrhh/contenido");
    } catch (err) {
      window.alert(mapApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton
          onClick={() => navigate("/rrhh/contenido")}
          sx={{ width: 36, height: 36, bgcolor: "secondary.500", "&:hover": { bgcolor: "secondary.600" } }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>

        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
              {isEditing ? "Editar evaluación" : "Crear evaluación"}
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      <Box sx={{ bgcolor: "white", borderRadius: "24px", p: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Stack spacing={2} sx={{ width: { xs: "100%", md: "38%" }, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.900" }}>
              Información General
            </Typography>

            <TextField
              label="Título"
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            />

            <TextField
              select
              label="Estado"
              value={form.status}
              onChange={handleChange("status")}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {STATUS_LABELS[option] || option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Autor"
              value={authorLabel}
              fullWidth
              size="small"
              disabled
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            />

            <Stack spacing={1}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
                Disponible desde
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Fecha inicio"
                  type="date"
                  value={form.availableFromDate}
                  onChange={handleChange("availableFromDate")}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />

                <TextField
                  label="Hora inicio"
                  type="time"
                  value={form.availableFromTime}
                  onChange={handleChange("availableFromTime")}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
                Disponible hasta
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Fecha fin"
                  type="date"
                  value={form.availableUntilDate}
                  onChange={handleChange("availableUntilDate")}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />

                <TextField
                  label="Hora fin"
                  type="time"
                  value={form.availableUntilTime}
                  onChange={handleChange("availableUntilTime")}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />
              </Stack>
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.noTimeLimit}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      noTimeLimit: event.target.checked,
                      timeLimitMinutes: event.target.checked ? "" : prev.timeLimitMinutes,
                    }))
                  }
                />
              }
              label="Sin tiempo límite de resolución"
            />

            {!form.noTimeLimit && (
              <TextField
                label="Tiempo límite en minutos"
                type="number"
                value={form.timeLimitMinutes}
                onChange={handleChange("timeLimitMinutes")}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
              />
            )}

            <Stack spacing={1}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                Alcance
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={scopeAll} onChange={handleToggleAll} />}
                  label="Aplicar a toda la empresa"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={scopeBranch}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setScopeBranch(checked);
                        if (checked) setScopeAll(false);
                        if (!checked) setSelectedBranchIds([]);
                      }}
                    />
                  }
                  label="Sucursal"
                />

                {scopeBranch && (
                  <TextField
                    select
                    size="small"
                    value={selectedBranchIds}
                    onChange={(event) =>
                      setSelectedBranchIds(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(",").filter(Boolean)
                      )
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        renderSelectedNames(selected, branches, "Seleccioná sucursales"),
                    }}
                    sx={{ ml: 4, maxWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={String(branch.id)}>
                        <Checkbox checked={selectedBranchIds.includes(String(branch.id))} />
                        {branch.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={scopeArea}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setScopeArea(checked);
                        if (checked) setScopeAll(false);
                        if (!checked) setSelectedAreaIds([]);
                      }}
                    />
                  }
                  label="Área"
                />

                {scopeArea && (
                  <TextField
                    select
                    size="small"
                    value={selectedAreaIds}
                    onChange={(event) =>
                      setSelectedAreaIds(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(",").filter(Boolean)
                      )
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        renderSelectedNames(selected, areas, "Seleccioná áreas"),
                    }}
                    sx={{ ml: 4, maxWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                  >
                    {areas.map((area) => (
                      <MenuItem key={area.id} value={String(area.id)}>
                        <Checkbox checked={selectedAreaIds.includes(String(area.id))} />
                        {area.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={scopeRole}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setScopeRole(checked);
                        if (checked) setScopeAll(false);
                        if (!checked) setSelectedRoleIds([]);
                      }}
                    />
                  }
                  label="Rol"
                />

                {scopeRole && (
                  <TextField
                    select
                    size="small"
                    value={selectedRoleIds}
                    onChange={(event) =>
                      setSelectedRoleIds(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(",").filter(Boolean)
                      )
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        renderSelectedNames(selected, roles, "Seleccioná roles"),
                    }}
                    sx={{ ml: 4, maxWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={String(role.id)}>
                        <Checkbox checked={selectedRoleIds.includes(String(role.id))} />
                        {role.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </FormGroup>

              <Button
                startIcon={<GroupsIcon />}
                onClick={loadEmployeesPreview}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "20px",
                  bgcolor: "secondary.200",
                  color: "primary.900",
                  "&:hover": { bgcolor: "secondary.300" },
                }}
              >
                Seleccionar empleados
              </Button>

              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "neutral.600" }}>
                {employeesPreviewLoading
                  ? "Calculando empleados alcanzados..."
                  : `Empleados alcanzados: ${selectedEmployeesTotal}`}
              </Typography>

              {selectedEmployeeIds.length > 0 && (
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "neutral.500" }}>
                  Seleccionados manualmente: {selectedEmployeeIds.length}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => navigate("/rrhh/contenido")}
                sx={{ textTransform: "none", fontWeight: 700, px: 3, borderRadius: "20px" }}
              >
                Cancelar
              </Button>

              <Button
                onClick={handleSave}
                disabled={loading || loadingReferences || saving}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  borderRadius: "20px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  "&:hover": { bgcolor: "primary.600" },
                  "&.Mui-disabled": { bgcolor: "neutral.200", color: "neutral.600" },
                }}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </Stack>

          <EvaluacionBodyBuilder
            questions={questions}
            setQuestions={setQuestions}
          />
        </Stack>
      </Box>

      <ScopeEmployeesModal
        open={employeesModalOpen}
        loading={employeesLoading}
        employees={employees}
        selectedEmployeeIds={selectedEmployeeIds}
        onToggleEmployee={toggleManualEmployee}
        onClose={() => setEmployeesModalOpen(false)}
      />
    </Stack>
  );
}
